import { useEffect } from 'react';

import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilState } from 'recoil';

import { Scenario } from 'components/types';

import { getScenariosForPlanUnits } from 'RestAPIs/scenario';

import { scenarioAtom } from '../../atoms';

import { Autocomplete } from 'components/shared';

type Props = {
  planUnitIds: number[];
  lastSavedScenarioId?: number | null;
};

const debouncedGet = debounce(getScenariosForPlanUnits, 200, {
  leading: true
});

const ScenarioSelect = ({ planUnitIds, lastSavedScenarioId }: Props) => {
  const { t } = useTranslation('common');
  const [scenario, setScenario] = useRecoilState(scenarioAtom);

  const { data: options = [], refetch } = useQuery(
    ['scenarios', planUnitIds],
    async ({ signal }) => {
      const response = await debouncedGet({ planUnitIds, signal });

      if (!response?.data) {
        return [];
      }

      return response.data
        .sort((a, b) => {
          if (a.isDefault) {
            return -1;
          }
          if (b.isDefault) {
            return 1;
          }

          return a.name.localeCompare(b.name);
        })

        .map((scenario) => ({
          label: `${scenario.name} ${
            scenario.isDefault ? t('scenario.default') : ''
          }`,
          value: scenario
        }));
    },
    {
      enabled: planUnitIds.length > 0
    }
  );

  useEffect(() => {
    return () => debouncedGet.cancel();
  }, [debouncedGet]);

  useEffect(() => {
    const updateOptions = async () => {
      await refetch();

      const lastSavedScenario = options?.find(
        ({ value }) => value.scenarioId === lastSavedScenarioId
      );

      if (lastSavedScenario) {
        setScenario(lastSavedScenario.value);
      }
    };

    if (planUnitIds.length) {
      updateOptions();
    }
  }, [lastSavedScenarioId]);

  useEffect(() => {
    if (planUnitIds.length && options?.length) {
      const defaultOption = options.find(({ value }) => value.isDefault);

      if (defaultOption) {
        setScenario(defaultOption.value);
      }
    } else {
      setScenario(null);
    }
  }, [planUnitIds, options]);

  const onScenarioSelected = (
    option: { label: string; value: Scenario } | null
  ) => {
    setScenario(option ? option.value : null);
  };

  return (
    <Autocomplete
      label={t('scenario.scenario')}
      options={options ?? []}
      onSelected={onScenarioSelected}
      value={
        options?.length
          ? options?.find(
              ({ value }) => value.scenarioId === scenario?.scenarioId
            ) ?? null
          : null
      }
      idProperty="id"
    />
  );
};

export default ScenarioSelect;
